/* eslint-disable max-len */
import { Box, Button, CircularProgress, IconButton, MenuItem, Paper, Select, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../components/Buttons/BackButton';
import ConditionalWrapper from '../../components/ConditionalWrapper/ConditionalWrapper';
import DialogAction from '../../components/Dialog/DialogAction';
import { RequestDict, RequestStatus } from '../../components/dictionaries/RequestStatus';
import GMapsPlaceCircle from '../../components/Maps/GMapsPlaceCircle';
import GMapsPlaceMarker from '../../components/Maps/GMapsPlaceMarker';
import { formatAzureFiles, formatMediaData, transformGreekToEnglish } from '../../lib/helperFuncs';
import HttpService from '../../services/HttpService';
import Theme from '../../Themes/main.theme';
import Attachments from '../../components/Attachments/Attachments';
import { DropdownOptions } from '../../components/form_v2/types';
import PersonIcon from '@mui/icons-material/Person';
import UserService from '../../services/UserService';
import { AdminAnswerApplicationPermissions, IntExt } from '../../lib/permissions';
import { ApiUsability, Title_Naming } from '../../lib/generalTypes';
import { getAllUsers } from '../../lib/static/users/fetchers';
import PageviewIcon from '@mui/icons-material/Pageview';
import { Stack } from '@mui/system';
import HorizontalWrapper from '../../components/Details/HorizonticalWrapper';
import Details from '../../components/Details/Details';
import AsyncBackdropSubmit from '../../components/AsyncBackdropSubmit';
import LinesSkeleton from '../../components/Skeletons/LinesSkeleton';
import { useQueueState } from '../../hooks/useQueueState';
import HorizontalStepperWithError from '../../components/StepperQueue';
import { useApiState } from '../../hooks/useApiState';
import { basicUserDbInfo } from '../../hooks/UserInfo';
import CarouselContainerForDetails from '../../components/Carousel/CarouselContainerForDetails';
import i18n from '../../i18n';


interface IData {
  id: number;
  title: string;
  afm: string;
  show_on_map: 'visible' | 'hidden' | 'sample';
  city: DropdownOptions;
  tk: string;
  address: string;
  address_number: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  latitude: number | null;
  longitude: number | null;
  thumbnail: string;
  responsible_user: {
    name: string | null;
    id: number | null;
  };
  is_request_responsible: boolean;
  is_applicant: boolean;
  protocol_number: number | null;
  submit_date: string;
  status: string;
  section: string;
  office: string;
  administrative_unit: {
    id: number | null;
    name: string | null;
    office_id: string | null;
  };
  applicant: {
    id: number;
    name: string;
  };
  comments: {
    comment: string; date_created: string
  }[];
  last_updated: string;
  attached_files: string[];
  media: string[]
  productions: string;
  website: string;
  facebook: string;
  youtube: string;
  linkedin: string;
  instagram: string;
  vimeo: string;
  category_level_1: { [key: string]: string }[]
  languages: { [key: string]: string }[];
  regions: { [key: string]: string }[];
  specialty: { [key: string]: string }[]
  services: string;
  services_en: string;
  productions_en: string;
  resume: string;
  type_of_application: number;
}

interface ISubmitValues {
  status?: string;
  responsible_user_id?: number | undefined | null;
}

const ViewProfApplication = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState(false);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [dialogMode, setDialogMode] = React.useState('');
  const [text, setText] = React.useState('');
  const [media, setMedia] = React.useState<any[]>([]);
  const [data, setData] = React.useState<IData>();


  const { pathname } = useLocation();

  const [newUser, setNewUser] = React.useState<number | undefined | null>();
  const [users, setUsers] =
    React.useState<{ key: string | null; value: number | null | undefined | string }[]>([]);

  const [shouldRenderActionButtons, setShouldRenderActionButtons] = React.useState<boolean>(false);
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { basicUserInfo, loading: userInfoLoading } = basicUserDbInfo();

  const [canInteractWithApplication, setCanInteractWithApplication] = React.useState<boolean>(false);


  const is_external = UserService.hasRole([IntExt.External]);

  const statusForComments = [
    RequestStatus.Under_Update, RequestStatus.Successful, RequestStatus.Rejected
  ];

  const statusForActionButtons = [
    RequestStatus.Cancelled, RequestStatus.Successful, RequestStatus.Rejected
  ];


  React.useEffect(() => {
    if (userInfoLoading || isLoading) return setCanInteractWithApplication(false);

    if (UserService.hasRole([AdminAnswerApplicationPermissions.PermissionAnswerProfessionalAll]))
      return setCanInteractWithApplication(true);

    return setCanInteractWithApplication(UserService.hasRole([AdminAnswerApplicationPermissions.PermissionAnswerProfessionalMyOrg]) && basicUserInfo.administrative_unit_id === data?.administrative_unit.id);
  }, [isLoading, userInfoLoading]);


  const getUsers = async (administrative_unit_id: number | undefined | null) => {
    setLoadingUsers(true);
    try {
      const resp = await getAllUsers({
        administrative_unit_id: administrative_unit_id,
        is_enabled: true
      });
      setUsers(resp.data.map((user: any) => {
        return {
          key: i18n.language === 'el'
            ? user.full_name
            : transformGreekToEnglish((t(user.full_name))),
          value: user.id
        };
      }));
    } catch (error) {
      console.error(error);
    }
    setLoadingUsers(false);
  };

  const basic_info = [
    [{
      name: 'id',
      value: data?.id
        ? String(data?.id)
        : '-'
    },
    {
      name: t('Αρμόδια Οργανική Μονάδα'),
      value: data?.administrative_unit.id
        ? is_external
          ? i18n.language === 'el'
            ? data.administrative_unit.name
            : t(data.administrative_unit.name as string)
          : <> <Stack direction='row'>
            <Typography>
              {data.administrative_unit.name}
            </Typography><Tooltip PopperProps={{
              disablePortal: true
            }} title={t('Προβολή')}>
              <IconButton color='primary' size='small'
                onClick={() => {
                  navigate(`/admin/units/${data.office}/${data.administrative_unit.office_id}`);
                }}>
                <PageviewIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          </>
        : '-'
    },
    {
      name: t('Τομέας'),
      value: i18n.language === 'el'
        ? data?.section
        : transformGreekToEnglish(t(data?.section as string)) || '-'
    }],
    [{
      name: t('Αριθμός Πρωτοκόλλου'),
      value: data?.protocol_number
        ? String(data?.protocol_number)
        : '-'
    },
    ...(!is_external
      ? [{
        name: t('Υπεύθυνος Χειριστής'),
        value: <Box display='flex'>
          {data?.responsible_user.id
            ? <>
              <Stack direction='row'>
                <Typography>
                  {i18n.language === 'el'
                    ? data?.responsible_user.name
                    : transformGreekToEnglish(t(data?.responsible_user.name as string))}
                </Typography><Tooltip PopperProps={{
                  disablePortal: true
                }} title={t('Προβολή')}>
                  <IconButton color='primary' size='small'
                    onClick={() => {
                      navigate(`/admin/users/${data?.responsible_user.id}`);
                    }}>
                    <PageviewIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </>
            : <Box mr={2}>-</Box>
          }
          {/* if (FO_Editor and !responsible_user_id) */}
          <Tooltip
            PopperProps={{
              disablePortal: true
            }} //title: Αναληψη Αιτηματος if FO_Editor
            title={t('Αλλαγή Χρήστη')}>
            {/* if FO_Editor handleUserChange() else setOpen(true)*/}
            <IconButton size='small' disabled={data?.status
              === RequestStatus.Draft
              || data?.status === RequestStatus.Completed
              || data?.status === RequestStatus.Successful
              || data?.status === RequestStatus.Rejected
            }
            onClick={() => {
              setNewUser(data?.responsible_user.id);
              setOpen(true);
              setDialogMode('select');
              getUsers(data?.administrative_unit.id);
            }}>
              <PersonIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      }]
      : []),
    {
      name: t('Λογαριασμός Αιτούντος'),
      value: data?.applicant.id
        ? <>
          <Stack direction='row'>
            <Typography>
              {i18n.language === 'el'
                ? data?.applicant.name
                : transformGreekToEnglish(t(data?.applicant.name))}
            </Typography>
            {!is_external && <Tooltip PopperProps={{
              disablePortal: true
            }} title={t('Προβολή')}>
              <IconButton color='primary' size='small'
                onClick={() => {
                  pathname.split('/').filter(Boolean)[0] === 'admin'
                    ? navigate(`/admin/users/${data?.applicant.id}`)
                    : navigate(`/visitor/details/${data?.applicant.id}`);
                }}>
                <PageviewIcon />
              </IconButton>
            </Tooltip>}
          </Stack>
        </>
        : '-'
    }],
    [{
      name: t('Ημερομηνία Υποβολής'),
      value: moment(data?.submit_date).format('DD/MM/YYYY')
    },
    {
      name: t('Κατάσταση'),
      value: data?.status
        ? i18n.language ==='el'
          ? RequestDict[data?.status]
          : transformGreekToEnglish(t(RequestDict[data?.status]))
        : '-'
    },
    {
      name: t('Τελευταία αλλαγή κατάστασης'),
      value: moment(data?.last_updated).format('DD/MM/YYYY')
    }]
  ];
  const basic_info_details = [
    [{
      name: t('Επωνυμία Επαγγελματία / Επιχείρησης'),
      value: i18n.language ==='el'
        ? data?.title
        : transformGreekToEnglish(t(data?.title as string)) || '-'
    },
    {
      name: t('Επώνυμο'),
      value: i18n.language ==='el'
        ? data?.last_name
        : transformGreekToEnglish(t(data?.last_name as any)) || '-'
    },
    {
      name: t('Όνομα'),
      value: i18n.language ==='el'
        ? data?.first_name
        : transformGreekToEnglish(t(data?.first_name as any)) || '-'
    },],
    [{
      name: t('ΑΦΜ'),
      value: data?.afm || '-'
    },
    {
      name: t('Τηλέφωνο'),
      value: data?.phone_number
    },
    {
      name: t('Email'),
      value: data?.email || '-'
    }]
  ];
  const rest_info = [
    [{
      name: t('Κατηγορία Υπηρεσιών'),
      value: data?.category_level_1 && data?.category_level_1.length > 0
        ? data?.category_level_1.map(category => i18n.language !=='en'
          ? category.key
          : transformGreekToEnglish(t(category.key as any)))
        : '-'
    },
    {
      name: t('Ειδικότητα Επαγγελματία'),
      value: data?.specialty && data?.specialty.length > 0
        ? data?.specialty.map(specialty => i18n.language ==='el'
          ? specialty?.key
          : transformGreekToEnglish(t(specialty?.key as any)))
        : '-'
    },
    ...(is_external ?   
      i18n.language !== "el" ? 
        [{
          name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
          value: data?.services_en as string || '-'
        },
        {
          name: t('Παραγωγές που έχετε συμμετάσχει'),
          value: data?.productions_en as string || '-'
        }]
        : 
        [{
          name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
          value: i18n.language ==='el'
            ? data?.services
            : transformGreekToEnglish(t(data?.services as string)) || '-'
        },{
          name: t('Παραγωγές που έχετε συμμετάσχει'),
          value: i18n.language ==='el'
            ? data?.productions
            : transformGreekToEnglish(t(data?.productions as string)) || '-'
        }
        ]
      :[{
        name: t('Παραγωγές που έχετε συμμετάσχει'),
        value: i18n.language ==='el'
          ? data?.productions
          : transformGreekToEnglish(t(data?.productions as string)) || '-'
      },
      {
        name: t('Παραγωγές που έχετε συμμετάσχει'),
        value: i18n.language ==='en'
          ? data?.productions_en
          : data?.productions_en as string || '-'
      },
      {
        name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
        value: i18n.language ==='el'
          ? data?.services
          : transformGreekToEnglish(t(data?.services as string)) || '-'
      },
      {
        name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
        value: i18n.language ==='en'
          ? data?.services_en
          : data?.services_en as string || '-'
      }]),
    {
      name: t('Περιοχές Εξυπηρέτησης / Παροχής Υπηρεσιών'),
      value: data?.regions && data?.regions.length > 0
        ? data?.regions.map(region => i18n.language ==='el'
          ? region?.key
          : transformGreekToEnglish(t(region?.key as any)))
        : '-'
    },
    {
      name: t('Γλώσσες Επικοινωνίας'),
      value: data?.languages && data?.languages.length > 0
        ? data?.languages.map(language => i18n.language ==='el'
          ? language?.key
          : transformGreekToEnglish(t(language?.key as any)))
        : '-'
    }],
    [{
      name: t('Website'),
      value: data?.website || '-'
    },
    {
      name: t('Facebook'),
      value: data?.facebook || '-'
    },
    {
      name: t('Youtube'),
      value: data?.youtube || '-'
    },
    {
      name: t('LinkedIn'),
      value: data?.linkedin || '-'
    },
    {
      name: t('Instagram'),
      value: data?.instagram || '-'
    },
    {
      name: t('Vimeo'),
      value: data?.vimeo || '-'
    }]
  ];
  const prof_location_details = [
    {
      name: t('Περιφέρεια / Δήμος'),
      value: i18n.language ==='el'
        ? data?.city?.key
        : transformGreekToEnglish(t(data?.city?.key as any))
    },
    {
      name: t('ΤΚ'),
      value: data?.tk
    },
    {
      name: t('Οδός'),
      value: i18n.language ==='el'
        ? data?.address
        : transformGreekToEnglish(t(data?.address as any))
    },
    {
      name: 'Αριθμός',
      value: data?.address_number
    }
  ];

  const SubmitValue = async ({ status, responsible_user_id }: ISubmitValues) => {
    try {
      status && setOpenBackdrop(true);
      const resp = await HttpService.getAxiosClient().put(`${process.env.REACT_APP_BACKEND_URL}/applications/professionals/${id}`, {
        ...(status && {
          status: status,
          ...(text && {
            free_text: text
          })
        }),
        ...(responsible_user_id && {
          responsible_user_id: newUser
        })
      });
      if (resp.status === 200) {
        if (responsible_user_id) {
          const user = users?.find((user) => user.value === responsible_user_id);
          setData({
            ...data as IData,
            responsible_user: {
              id: Number(user?.value),
              name: user?.key || ''
            }
          });
          setShouldRenderActionButtons(resp.data.is_responsible);
        }
        enqueueSnackbar(resp.data.message, { variant: resp.data.action });
        if (status) {
          if (pathname.split('/').filter(Boolean)[0] === 'admin') {
            navigate('/admin/applications/dashboard');
          } else navigate('/my-applications');
        }
      }
    } catch (error) {
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα'), { variant: 'error' });
      console.error(error);
    }
    status && setOpenBackdrop(false);
  };

  const handleDialogProps = () => {
    switch (dialogMode) {
    case 'select':
      return {
        ...(users.length === 0 && { mode: 'info' as 'info' | 'delete' | 'edit' }),
        handleClose: () => {
          setOpen(false);
          setNewUser(data?.responsible_user.id); //reset
        },
        title: t('Αλλαγή ανάθεσης σε'),
        content: loadingUsers
          ? <Box display='flex' justifyContent='center'>
            <CircularProgress />
          </Box>
          : users.length === 0
            ? t('Δεν υπάρχουν διαθέσιμοι Ενεργοί Χρήστες στην συγκεκριμένη Οργανική Μονάδα')
            : <Select
              MenuProps={{
                disablePortal: true
              }}
              fullWidth
              value={newUser}
              onChange={(e) => setNewUser(e.target.value as number)}
            >
              {users.map((option) => {
                return (
                  <MenuItem key={option.value} value={option.value as any}>
                    {option.key}
                  </MenuItem>
                );
              })}
            </Select>,
        onAccept: () => {
          SubmitValue({ responsible_user_id: newUser });
          setOpen(false);
        }
      };
    case 'cancelled':
      return {
        fullWidth: true,
        open: open,
        handleClose: () => {
          setOpen(false);
          setText('');
        },
        title: t('Προαιρετική Προσθήκη Σχολίων'),
        content: <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder={t('Ενημερώστε τον λόγο ακύρωσης του αιτήματος.') as string}
        />,
        onAccept: () => {
          SubmitValue({ status: dialogMode });
          setOpen(false);
        },
      };
    case 'rejected':
      return {
        fullWidth: true,
        open: open,
        handleClose: () => {
          setOpen(false);
          setText('');
        },
        title: t('Δικαιολόγηση Απόρριψης'),
        content: <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder={t('Ενημερώστε τον χρήστη για τους λόγους απόρριψης του αιτήματος.') as string}
        />,
        onAccept: () => {
          SubmitValue({ status: dialogMode });
          setOpen(false);
        },
        disableAcceptButton: !text
      };
    case 'under_update':
      return {
        fullWidth: true,
        open: open,
        handleClose: () => {
          setOpen(false);
          setText('');
        },
        title: t('Δικαιολόγηση Επικαιροποίησης'),
        content: <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder={t('Ενημερώστε τον χρήστη για τις αλλαγές που χρειάζεται να πραγματοποιήσει.') as string}
        />,
        onAccept: () => {
          SubmitValue({ status: dialogMode });
          setOpen(false);
        },
        disableAcceptButton: !text
      };
    case 'successful':
      return {
        fullWidth: true,
        open: open,
        handleClose: () => {
          setOpen(false);
          setText('');
        },
        title: t('Προαιρετική Προσθήκη Σχολίων'),
        content: <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder={t('Προσθέστε εάν κρίνετε απαραίτητο κάποια σχόλια σχετικά με την συγκεκριμένη καταχώριση.') as string}
        />,
        onAccept: () => {
          SubmitValue({ status: dialogMode });
          setOpen(false);
        }
      };
    default:
      return {
        handleClose: () => null,
        title: '',
        content: <></>,
        onAccept: () => null
      };
    }
  };

  const getData = async () => {
    try {
      const resp = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/applications/professionals/${id}`);
      setData(resp.data);
      setShouldRenderActionButtons((resp?.data?.is_request_responsible) &&
        !statusForActionButtons.includes(resp?.data?.status));
      const { images, videos, thumbnail } = formatMediaData(resp.data);
      setMedia([thumbnail, ...images, ...videos]);
      setNewUser(resp.data.responsible_user.id);
      data?.responsible_user.id
        ? setUsers([{ key: data?.responsible_user.name, value: data?.responsible_user.id }])
        : setUsers([]);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    getData();
  }, [shouldRenderActionButtons]);

  document.title = data?.type_of_application !== Title_Naming.UpdateProfessional
    ? is_external
      ? t('Αίτημα Καταχώρισης Επαγγελματία / Επιχείρησης')
      : t('Εσωτερικό Αίτημα Καταχώρισης Επαγγελματία/Επιχείρησης')
    : t('Αίτημα Αλλαγής Στοιχείων Επαγγελματία/Επιχείρησης');


  const { activeQueueStep, stepFailed } = useQueueState(id);
  const { apiEnabled, loadingApi } = useApiState(ApiUsability.Google_Maps, true);

  return (
    <>

      <Box mt={2} mb={2}>
        <BackButton
          onClick={() => pathname.split('/').filter(Boolean)[0] === 'admin'
            ? navigate('/admin/applications/dashboard')
            : navigate('/my-applications')
          }
        />
      </Box>

      {data && <Typography variant='h2' color={Theme.labelColor?.main}>
        {data?.type_of_application !== Title_Naming.UpdateProfessional
          ? is_external
            ? t('Αίτημα Καταχώρισης Επαγγελματία / Επιχείρησης')
            : t('Εσωτερικό Αίτημα Καταχώρισης Επαγγελματία/Επιχείρησης')
          : t('Αίτημα Αλλαγής Στοιχείων Επαγγελματία/Επιχείρησης')}
      </Typography>}
      {/* Basic info of the Request */}
      <Box >

        {data && !is_external && data.status == RequestStatus.In_Progress && <>
          <Box sx={{ backgroundColor: Theme.labelColor?.background, py: 1, pl: 1, ml: 1, my: 2 }}>
            <Typography variant='h3' color={Theme.labelColor?.main}>
              {t('Κατάσταση Αιτήματος για εισαγωγή στο Omeka')}
            </Typography>
          </Box>
          <HorizontalStepperWithError
            activeStep={activeQueueStep}
            failedStep={stepFailed}
          />
        </>
        }

        <Box sx={{ backgroundColor: Theme.labelColor?.background, py: 1, pl: 1, ml: 1, my: 2 }}>
          <Typography variant='h3' color={Theme.labelColor?.main}>
            {t('Βασικές Πληροφορίες Αιτήματος')}
          </Typography>
        </Box>
        {isLoading ?
          <LinesSkeleton lines={3} /> :
          <HorizontalWrapper
            details={basic_info}
            marginLeft />
        }
      </Box>
      {/* comments section */}
      {!isLoading && data?.status &&
        statusForComments.includes(data?.status) &&
        (data?.comments && data?.comments.length > 0)
        ? <>
          <Box sx={{ backgroundColor: Theme.labelColor?.background, py: 1, pl: 1, ml: 1, my: 2 }}>
            <Typography variant='h3' color={Theme.labelColor?.main}>
              {t('Σχόλια από Υπεύθυνο Χειριστή')}
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column'
            height='100px' overflow='auto'
            marginLeft='16px' gap={2}>
            {data?.comments?.map((comm) => {
              return (
                <Box key={JSON.stringify(comm)}>
                  <Typography fontWeight='500'>
                    {moment(comm.date_created).format('DD/MM/YYYY hh:mm:ss')}
                  </Typography>
                  <Typography variant='body1' sx={{ mt: 1 }}>
                    {comm.comment}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
        : null
      }
      {/* Request Details */}
      <Box>
        <Box sx={{ backgroundColor: Theme.labelColor?.background, py: 1, pl: 1, ml: 1, my: 2 }}>
          <Typography variant='h3' color={Theme.labelColor?.main}>
            {t('Λεπτομέρειες Αιτήματος')}
          </Typography>
        </Box>
        {isLoading ?
          <LinesSkeleton lines={7} /> :
          <>
            <HorizontalWrapper
              title={t('Βασικά Στοιχεία Επαγγελματία / Επιχείρησης')}
              details={basic_info_details}
              marginLeft />
            <HorizontalWrapper
              title={t('Λοιπά Στοιχεία Επιχείρησης / Επαγγελματία')}
              details={rest_info}
              marginLeft />
          </>}
        <Box sx={{ ml: '10px', mt: 2 }}
          minWidth='733px'>
          <Paper variant='outlined' sx={{ height: 'fit-content', borderRadius: 2, boxShadow: 0, backgroundColor: 'transparent' }}>
            <Typography m={2} variant='h3'>{t('Στοιχεία Έδρας Επιχείρησης')}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' } }} >
              <Box width='45%' m={2}>
                {isLoading ?
                  <LinesSkeleton lines={5} /> :
                  <Details rows={prof_location_details}
                    removeLines
                  />}
              </Box>
              {(apiEnabled && !loadingApi)
                ? <Box width='700px' height='250px'
                  m={2}>
                  <ConditionalWrapper condition={isLoading}
                    wrapper={(children) =>
                      <Skeleton variant='rectangular' width={500}>{children}</Skeleton>}>
                    {data?.show_on_map === 'sample'
                      ? <GMapsPlaceCircle
                        location={{
                          lat: Number(data?.latitude),
                          lng: Number(data?.longitude)
                        }}
                      />
                      :
                      <GMapsPlaceMarker
                        zoom={12}
                        location={data?.show_on_map === 'hidden'
                          ? undefined
                          : {
                            lat: Number(data?.latitude),
                            lng: Number(data?.longitude)
                          }
                        }
                      />
                    }
                  </ConditionalWrapper>
                </Box>
                : null
              }
            </Box>
          </Paper>
        </Box>
        {(data?.thumbnail) || (data?.media && data?.media.length > 0)
          ? <Box sx={{ ml: '10px', mt: 2 }}>
            <Paper variant='outlined' sx={{ height: 'fit-content', borderRadius: 2, boxShadow: 0, backgroundColor: 'transparent' }}>
              <Box m={2}>
                <CarouselContainerForDetails title={t('Φωτογραφίες / Βίντεο') as string} 
                  media={media}
                  imageButtons={false}/>
              </Box>
            </Paper>
          </Box>
          : null
        }
        {data?.resume
          ? <Box sx={{ ml: '10px', mt: 2 }}>
            <Paper variant='outlined' sx={{ height: 'fit-content', borderRadius: 2, boxShadow: 0, backgroundColor: 'transparent' }}>
              <Typography m={2} variant='h3'>
                {t('Βιογραφικό Σημείωμα')}
              </Typography>
              <Box m={1} display='flex'
                gap={10} minWidth={350}>
                <Attachments attachments={formatAzureFiles(data?.resume)} />
              </Box>
            </Paper>
          </Box>
          : null
        }
        {data?.attached_files && data?.attached_files.length > 0
          ? <Box sx={{ ml: '10px', mt: 2 }}>
            <Paper variant='outlined' sx={{ height: 'fit-content', borderRadius: 2, boxShadow: 0, backgroundColor: 'transparent' }}>
              <Typography m={2} variant='h3'>
                {t('Άλλα Επισυναπτόμενα Έγγραφα')}
              </Typography>
              <Box m={1} display='flex'
                gap={10} minWidth={350}>
                <Attachments attachments={formatAzureFiles(data?.attached_files)} />
              </Box>
            </Paper>
          </Box>
          : null
        }
      </Box>

      {/* Applicant Buttons */}
      {data?.is_applicant &&
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5, gap: 2 }}>
          {(data?.status === RequestStatus.Draft || data?.status === RequestStatus.Under_Update)
            ? <Button variant='outlined' color='error'
              onClick={() => {
                setOpen(true);
                setDialogMode(RequestStatus.Cancelled);
              }}>
              {t('Ακύρωση Αιτήματος')}
            </Button>
            : null
          }
          {data?.status === RequestStatus.Draft ?
            <Button variant='outlined'
              onClick={() => navigate(`/professionals/edit/${id}`)}>
              {t('Συνέχιση Επεξεργασίας')}</Button>
            : null
          }
          {data?.status === RequestStatus.Under_Update ?
            <Button variant='contained' onClick={() => (pathname.split('/').filter(Boolean)[0] === 'admin') ?
              navigate(`/admin/professionals/edit/${id}`) :
              navigate(`/professionals/edit/${id}`)}>
              {t('Επαναυποβολή')}</Button>
            : null
          }
        </Box>
      }
      {!is_external && <Box m={5} >
        {
          !canInteractWithApplication &&
          <Typography textAlign={'center'} variant='h3'>{t('Δεν έχετε δικαίωμα να αλληλεπιδράσετε με την αίτηση, επικοινωνείστε με τον προϊστάμενο σας')}</Typography>
        }
      </Box>}
      {shouldRenderActionButtons && canInteractWithApplication &&

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5, gap: 2 }}>
          <Button variant='outlined' color='error'
            onClick={() => {
              setOpen(true);
              setDialogMode(RequestStatus.Rejected);
            }}>
            {t('Απόρριψη Καταχώρισης')}
          </Button>
          <Button variant='outlined'
            onClick={() => {
              setOpen(true);
              setDialogMode(RequestStatus.Under_Update);
            }}>
            {t('Επικαιροποίηση Καταχώρισης')}</Button>
          <Button variant='contained' onClick={() => {
            setOpen(true);
            setDialogMode(RequestStatus.Successful);
          }}>
            {t('Έγκριση Καταχώρισης')}</Button>
        </Box>
      }
      {/* Modal */}
      <DialogAction fullWidth open={open}
        {...handleDialogProps()} />
      <AsyncBackdropSubmit isLoading={openBackdrop}>
        {t('Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...')}
      </AsyncBackdropSubmit>
    </>
  );
};

export default ViewProfApplication;